import { type ReactNode, useMemo } from 'react';
import { Outlet } from 'react-router';
import { useCan } from '@/features/access-control';
import { UserMenu, useSession } from '@/features/auth';
import { CompanyScopeSelector } from '@/features/company-scope';
import { ReportExportsMenu } from '@/features/report-exports';
import { OfflineBanner } from '@/shared/ui/feedback';
import { navigationFor } from './navigation';
import { NavigationMenu } from './navigation-menu';

/**
 * Layout das telas autenticadas: menu lateral conforme permissões no escopo (barra horizontal
 * em telas estreitas) e cabeçalho com o seletor de escopo, as exportações e o usuário.
 */
export function AppLayout(): ReactNode {
  const can = useCan();
  const platformAdmin = useSession((state) => state.session?.platformAdmin === true);
  const items = useMemo(() => navigationFor(can, { platformAdmin }), [can, platformAdmin]);
  return (
    <div className="min-h-screen md:grid md:grid-cols-[14rem_1fr]">
      <aside className="border-b bg-muted/40 md:min-h-screen md:border-r md:border-b-0">
        <div className="flex items-center gap-4 px-4 py-3 md:flex-col md:items-stretch md:gap-6 md:py-5">
          <span className="text-lg font-semibold">JAF</span>
          {items.length > 0 && (
            <nav aria-label="Principal" className="overflow-x-auto">
              <NavigationMenu items={items} />
            </nav>
          )}
        </div>
      </aside>
      <div className="min-w-0">
        <OfflineBanner />
        <header className="border-b">
          <div className="flex flex-wrap items-center justify-end gap-4 px-4 py-3">
            <CompanyScopeSelector />
            <ReportExportsMenu />
            <UserMenu />
          </div>
        </header>
        <main className="mx-auto max-w-6xl px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
